// Споделени типове, константи и чисти помощници за работния лист „Доставка"
// (DeliveryWorksheetPage + подкомпонентите в тази папка). Извадени от
// монолитния DeliveryWorksheetPage при разделянето му (правило „без огромни
// файлове"); поведението е НЕПРОМЕНЕНО.
import dayjs from "dayjs";
import type { Delivery } from "../../../../api/inventory";
import { formatUnitPrice } from "../../components/numbers";
import { SHEET_INPUT } from "../../components/list/density";
import {
  isVersionedObject,
  localDraftStore,
  type DraftPayloadBase,
  type DraftSummaryFields,
} from "../../components/drafts/draftModel";
import { formatLineSum, savedRowTypedSum } from "./deliveryRowMath";

// ─── Числови помощници (парсерът приема запетая И точка) ───────────────────
export const parseDecimal = (raw: unknown): number => {
  const value = parseFloat(
    String(raw ?? "")
      .replace(/\s/g, "")
      .replace(",", "."),
  );
  return Number.isNaN(value) ? 0 : value;
};
// Цена/сума с 2 знака и ТОЧКА (стойността се ползва и като input); половинката
// нагоре по десетичния запис, както бекендът
export const formatPrice = formatLineSum;
// Ед. цената живее в общия `components/numbers.ts` (ползва я и разнасянето).
export { formatUnitPrice, UNIT_PRICE_DECIMALS } from "../../components/numbers";
// Количество със ЗАПЕТАЯ, точно `decimals` знака (по мерната единица): 3 →
// „5,000"; 0 → „5". Без групиране на хилядите (стойността се ползва и като input).
export const formatQtyInput = (value: number, decimals = 3) =>
  value.toFixed(decimals).replace(".", ",");

// ─── Данни на ред (само „докоснатите" продукти живеят в state) ─────────────
// ИМЕНАТА на полетата са част от формата на черновата (v4) — в localStorage и
// на сървъра. НЕ ги преименувай.
export type DeliveryRowDraft = {
  packagingId: number | null; // null = базова мерна единица
  qty: string; // брой разфасовки (без разфасовка = базови единици)
  price: string; // ед. цена за БАЗОВА единица, ВИНАГИ нето
  net: string;
  gross: string;
  batch: string;
  expiry: string; // yyyy-MM-dd
  warehouseId: number | null;
  priceAuto: boolean; // цена от последна доставка (сива, курсив)
  // Маркиран проблем („Повредена · 3 бутилки счупени"). Добавено с
  // редизайна — старите чернови го нямат.
  problem?: string | null;
  // Коя СУМА е въвел операторът — тя е източникът на реда и не се пренаписва;
  // цената се извежда от нея (`deliveryRowMath`). Липсва/null = въведена е ед.
  // цената (и всички стари чернови). Записът подава сумата като `netSum`.
  typedSum?: "net" | "gross" | null;
  // Брутото, записано в документа (само при редакция): остава, докато е
  // съвместимо с нетото и ставката — въведено бруто не „мърда" със стотинка.
  savedGross?: string | null;
  // „Коригирай общото": колко от разликата с въведеното общо е поел редът
  // (+0.02). Всяка ръчна промяна по реда го чисти.
  totalAdjust?: number | null;
};
export const emptyDeliveryRow = (
  warehouseId: number | null,
): DeliveryRowDraft => ({
  packagingId: null,
  qty: "",
  price: "",
  net: "",
  gross: "",
  batch: "",
  expiry: "",
  warehouseId,
  priceAuto: false,
});

export const isRowTouched = (row: DeliveryRowDraft) =>
  row.qty !== "" ||
  row.price !== "" ||
  row.batch !== "" ||
  row.expiry !== "" ||
  row.packagingId != null ||
  // въведена сума без количество — редът иска количество, не изчезва
  (!!row.typedSum && (row.net !== "" || row.gross !== ""));

// Ред на продуктите както са в документа (за таблицата при редакция/темплейт)
export const orderOfLines = (delivery: Delivery): number[] =>
  Array.from(new Set(delivery.lines.map((line) => line.productId)));

// ─── Групиране на редове на доставка → по продукт (редакция/темплейт) ──────
// `keepSavedSums` (редакция): редът носи ЗАПИСАНИТЕ нето/бруто, а не ги смята
// наново от цената — сумата е въведената и остава същата при запис без промяна.
// Темплейтът („От минала доставка") взема само количеството и цената.
export const groupDeliveryLines = (
  delivery: Delivery,
  decimalsOf: (productId: number) => number,
  { keepSavedSums = false }: { keepSavedSums?: boolean } = {},
): Record<number, DeliveryRowDraft> => {
  const linesByProduct = new Map<number, Delivery["lines"]>();
  for (const line of delivery.lines) {
    const bucket = linesByProduct.get(line.productId);
    if (bucket) bucket.push(line);
    else linesByProduct.set(line.productId, [line]);
  }
  const out: Record<number, DeliveryRowDraft> = {};
  for (const [productId, lines] of linesByProduct) {
    const first = lines[0];
    const samePackaging = lines.every(
      (l) => (l.packagingId ?? null) === (first.packagingId ?? null),
    );
    const row = emptyDeliveryRow(first.warehouseId);
    if (samePackaging) {
      row.packagingId = first.packagingId ?? null;
      row.qty = formatQtyInput(
        lines.reduce((sum, l) => sum + (l.packagingsCount ?? l.quantity), 0),
        decimalsOf(productId),
      );
    } else {
      row.packagingId = null;
      row.qty = formatQtyInput(
        lines.reduce((sum, l) => sum + l.quantity, 0),
        decimalsOf(productId),
      );
    }
    // `formatUnitPrice`, НЕ `formatPrice`: записаната цена може да е с 5 знака
    // (изведена от сумата) и рязането ѝ тук би пренаписало сумите при първото
    // отваряне за редакция.
    row.price = formatUnitPrice(first.unitPrice);
    if (keepSavedSums) {
      // `sum` = себестойността (нето); `sumNoVat` на заварен ред без снимка е
      // сметнат обратно от брутото и НЕ е записаното нето
      const savedNet = lines.reduce((sum, l) => sum + l.sum, 0);
      const baseQuantity = lines.reduce((sum, l) => sum + l.quantity, 0);
      row.net = formatLineSum(savedNet);
      row.gross = formatLineSum(lines.reduce((sum, l) => sum + l.sumWithVat, 0));
      row.savedGross = row.gross;
      row.typedSum = savedRowTypedSum(baseQuantity, row.price, savedNet);
    }
    row.batch = first.batchNumber ?? "";
    // UTC ISO → ЛОКАЛНА дата, иначе срокът се мести с ден назад при редакция
    row.expiry = first.expiryDate
      ? dayjs(first.expiryDate).format("YYYY-MM-DD")
      : "";
    out[productId] = row;
  }
  return out;
};

// ─── Чернова (localStorage + сървърът, `components/drafts/`) ────────────────
// Същият обект е и `payload` на сървърната чернова (тип „delivery").
export type DeliveryDraft = DraftPayloadBase & {
  v: 4;
  date: string;
  invoiceNumber: string;
  supplierId: number | null;
  whId: number | null;
  note: string;
  rows: Record<number, DeliveryRowDraft>;
  order: number[]; // редът на добавяне на продуктите в таблицата
  // Добавено с редизайна (незадължително — v3/v4 чернови го нямат)
  documentType?: "invoice" | "note";
};
const parseDeliveryDraft = (raw: unknown): DeliveryDraft | null => {
  if (!isVersionedObject(raw, [3, 4])) return null;
  const draft = raw as unknown as Omit<DeliveryDraft, "v" | "order"> & { order?: number[] };
  if (!draft.rows || typeof draft.rows !== "object") return null;
  // v3 (без order) → редът по ключовете на редовете
  return { ...draft, v: 4, order: draft.order ?? Object.keys(draft.rows).map(Number) };
};
export const DELIVERY_DRAFT_STORE = localDraftStore("inventory_delivery_draft", parseDeliveryDraft);
export const readDraft = DELIVERY_DRAFT_STORE.read;
export const clearDraft = DELIVERY_DRAFT_STORE.clear;
// Има ли нещо за продължаване (празният лист не е чернова)
export const hasDeliveryDraftContent = (draft: DeliveryDraft) => Object.values(draft.rows).some(isRowTouched);
// Картата на локалната чернова на началния екран (сървърната носи същото)
export const localDeliveryDraftSummary = (
  draft: DeliveryDraft,
  supplierNameOf: (supplierId: number | null) => string | null,
): DraftSummaryFields | null => {
  const touched = Object.values(draft.rows).filter(isRowTouched);
  if (!touched.length) return null;
  return {
    label: supplierNameOf(draft.supplierId),
    documentType: draft.documentType ?? null,
    linesCount: touched.length,
    total: touched.reduce((sum, row) => sum + parseDecimal(row.gross), 0),
  };
};

// ─── Оцветяване на съвпадналите думи в резултат на търсачката ───────────────
// Оцветяването на съвпаденията е ОБЩО за модула (един файл, три търсачки).
export { highlightWords } from "../../components/highlight";

// ─── Решетка / клетки ───────────────────────────────────────────────────────
// grid-template-columns на решетката: # · продукт · разфас. · кол. · ед. цена ·
// ДДС · нето · бруто · партида · срок · склад · ⚑ ✕
export const LINE_GRID_CLASS =
  "grid grid-cols-[44px_minmax(220px,1.4fr)_130px_110px_104px_76px_92px_92px_96px_130px_130px_76px]";
export const LINE_GRID_MIN_WIDTH_CLASS = "min-w-[1360px]";
// колоните на LINE_GRID_CLASS — за пълнежа над реда „Общо"
export const LINE_GRID_COLUMN_COUNT = 12;

// колони на падащия списък на търсачката (име · наличност · посл. д-ка · цена)
export const DROPDOWN_GRID_CLASS = "grid grid-cols-[minmax(0,1fr)_180px_260px_110px]";

// Плоска клетка (еквивалентът на стария .dwx-cell): прозрачна рамка/фон, на
// фокус — оранжева рамка + бял фон + ring; фокусът маха и курсива на
// автоматичната цена (focus:not-italic). leading-[normal] = line-height като
// от `font:` shorthand-а (иначе клетките стават по-високи).
export const FLAT_CELL_CLASS =
  "box-border w-full rounded-[5px] border-[1.5px] border-transparent bg-transparent leading-[normal] outline-none " +
  "focus:border-brand focus:bg-surface focus:not-italic focus:shadow-[0_0_0_3px_rgba(217,127,0,0.15)]";
// Червена рамка при липсващо задължително поле (стария .dwx-err)
export const FLAT_CELL_ERROR_CLASS = "!border-danger";

// Затворената „тежка" клетка (PickCell) изглежда като плоското поле — същият
// стил и височината на реда (`SHEET_INPUT`); стойността вътре
// (`<span className="truncate">`) се реже с „…"
export const pickCellClassOf = (muted: boolean) =>
  `${FLAT_CELL_CLASS} ${SHEET_INPUT} flex min-w-0 cursor-pointer items-center px-[9px] text-[13px] ${
    muted ? "text-ghost" : "text-body"
  }`;

// Навигируеми колони (еднакви клетки): 0 разфас · 1 кол. · 2 цена · 3 нето ·
// 4 бруто · 5 партида · 6 срок · 7 склад
export const COL_COUNT = 8;
export const PICK_COLS = new Set([0, 6, 7]);
